import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { classnames } from 'Utils/classnames/classnames.utils';
import { constants } from '@clubmed/utils';
import { BEFORE_APP } from 'Components/frameworks/portal/Portal.constants';
import { getActivityTypeName } from 'Components/utils/booking/bookingUtils';
import { TOAST_DURATION } from 'Components/utils/booking/booking-additional-services/BookingAddtionalServices.utils';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { ActivitiesContext } from 'Context/ActivitiesContext';
import { useAppContext } from 'Context/ReactAppContext';
import { usePrevious } from 'Src/hooks/use-previous/usePrevious';
import { getKeyValue, t } from 'Src/i18n';
import { getReservedServicesByAttendee } from 'Store/booking/booking.selector';
import { deleteAdditionalServices } from 'Store/booking/proposals/proposals.action';
import { TRACK_BE_EVENT_GA_ACTIVITIES } from 'Tracking/events/be.tracking';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { Title } from 'Components/frameworks/title/Title';
import { Icon } from 'Components/frameworks/icon/Icon';
import { ICONS } from 'Components/utils/icon/icons';
import { AccommodationSummary } from 'Components/widgets/accommodation-summary/AccommodationSummary';
import { BottomNavbarLayer } from 'Components/widgets/bottom-navbar-layer/BottomNavbarLayer';
import { ReservedServicesList } from 'Components/widgets/reserved-services-wrapper/ReservedServicesList';
import {
  getPriceDetail,
  getSummaryViewLinesByProposal,
  getOffersAndDiscounts,
} from 'Views/booking/summary/SummaryView.utils';
import { PromoCode } from 'Components/widgets/promo-code/PromoCode';
import { DiscountsLine } from 'Components/widgets/promo-code/DiscountsLine';
import { AccordionBlock } from 'Components/widgets/accordion-block/AccordionBlock';
import { PriceTicketFavorite } from 'Components/widgets/bottom-navbar-layer/PriceTicketFavorite';
import { BottomNavbarTotalPrice } from 'Components/widgets/bottom-navbar-layer/BottomNavbarTotalPrice';
import { SelectedRate } from 'Components/widgets/bottom-navbar-layer/SelectedRate';
import { PriceDetailLines } from 'Components/widgets/price-detail-block-lines/PriceDetailLines';
import { getTotalPriceWithDonation } from 'Components/utils/price/price.util';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { isAllowedPaymentPlan } from 'Components/widgets/payment-info/buy-now-pay-later/BuyNowPayLater.utils';
import { GreatMembersLoyaltyPoints } from 'Components/widgets/great-members-loyalty-points/GreatMembersLoyaltyPoints';
import {
  BUY_NOW_PAY_LATER_PRICING,
  BUY_NOW_PAY_LATER_PROVIDER,
} from 'Components/widgets/payment-info/buy-now-pay-later/BuyNowPayLater.const';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { INSTALMENT_LOCALES } from 'Components/frameworks/price/Price';
import { OptionBlock } from './OptionBlock';
const {
  FLEX_RATES,
  PACKAGES: { JOYVIEW_ALL_INCLUSIVE, JOYVIEW_FOOD_AND_BEVERAGES },
} = constants;
type Props = {
  closePanel: () => void;
  continueAction: () => void;
  currentPage: string;
  hideContinueButton: boolean;
  isDisabled: boolean;
  i18n: CMSContent;
  isFavorite: boolean;
  isNextStep: boolean;
  isOnLastPage?: boolean;
  isPriceLoading: boolean;
  navbarRef?: ReactRef;
  nextPage?: string | null;
  noEdit?: boolean;
  continueBtn?: any;
  overwriteContinueButton?: boolean;
  portalPosition?: string;
  previousAction: () => void;
  previousPage?: string | null;
  price: ProposalPriceDonation;
  proposal: Proposal;
  paymentLabel?: string;
  resort: Product;
  shouldOpenPanel: boolean;
  isOptionHoldOnly: boolean;
  onClickFavorite: () => void;
};

const getTotalRentedServices = (
  services: ReadonlyArray<ReservedServicesByAttendee> | null | undefined,
) => (services || []).reduce((acc, s) => acc + s.rentedServices.length, 0);

export const BookingBottomNavbar = ({
  continueAction,
  hideContinueButton,
  currentPage,
  isDisabled,
  i18n,
  isFavorite,
  isNextStep,
  isOnLastPage,
  isPriceLoading,
  navbarRef,
  continueBtn,
  noEdit,
  overwriteContinueButton,
  portalPosition = BEFORE_APP,
  previousAction,
  price,
  proposal,
  resort,
  shouldOpenPanel,
  closePanel,
  nextPage,
  previousPage,
  paymentLabel = t('booking.priceDetails.packagePrice.bookingLabel'),
  onClickFavorite,
}: Props) => {
  const appContext = useAppContext();
  const { isAppMounted, isDesktop, isTablet } = useResponsive();
  const { trackClickEvent } = useTrackEvent();
  const isMobile = !isTablet;
  const [shouldCloseAllLayers, setShouldCloseAllLayers] = useState(false);
  const reservedServicesByAttendee = useSelector(getReservedServicesByAttendee);
  const layerRef: {
    current: HTMLElement | null | undefined;
  } = useRef();
  const prevServicesLength = usePrevious(getTotalRentedServices(reservedServicesByAttendee));
  const [addedServicesNumber, setAddedServicesNumber] = useState(0);
  const priceWithDonation = getTotalPriceWithDonation(proposal.price, appContext.locale);
  const { isJoyview, isUrbanOasis } = resort;
  const isJoyViewAllInclusive = proposal.packageId === JOYVIEW_ALL_INCLUSIVE && isJoyview;
  const isUrbanOasisAllInclusive = proposal.packageId === JOYVIEW_ALL_INCLUSIVE && isUrbanOasis;
  const isUrbanOasisFoodAndBeverage =
    proposal.packageId === JOYVIEW_FOOD_AND_BEVERAGES && isUrbanOasis;
  const shouldShowPriceDetails =
    isJoyViewAllInclusive || isUrbanOasisAllInclusive || (!isJoyview && !isUrbanOasis);
  const hasSelectedRate = Boolean(price?.hasFlexRates && price?.selectedRate && !isPriceLoading);
  const displayOptionAvailable =
    proposal.optionAvailable &&
    currentPage !== 'banking' &&
    isFeatureFlipped(appContext)('booking.option');
  const displayPromoCode = currentPage !== 'banking';
  const isPromoCodeEnabled = getKeyValue('booking.priceDetails.promoCode.enabled');
  const { discounts, offers } = getOffersAndDiscounts(proposal?.price?.discounts || []);
  const enableBuyNowPayLaterProvider = BUY_NOW_PAY_LATER_PROVIDER.find((id) => {
    return (
      isFeatureFlipped(appContext)(`psp.${id.toLowerCase()}`) &&
      isAllowedPaymentPlan({
        id,
        price: price?.total?.value,
        startDate: proposal.arrivalDate,
      })
    );
  });
  const BuyNowPayLaterPricing = BUY_NOW_PAY_LATER_PRICING[enableBuyNowPayLaterProvider];
  const dispatch = useDispatch();
  const roomQuantity = proposal.accommodations
    .filter((accommodation) => accommodation.quantity > 1)
    .map((accommodation) => accommodation.quantity);
  useEffect(() => {
    const daddedServicesNumber =
      getTotalRentedServices(reservedServicesByAttendee) - prevServicesLength;
    setAddedServicesNumber(daddedServicesNumber);
    const timer = setTimeout(() => setAddedServicesNumber(0), TOAST_DURATION);
    return () => clearTimeout(timer);
  }, [reservedServicesByAttendee]);
  const track = useTracking(TRACK_BE_EVENT_GA_ACTIVITIES);
  const displayInstalment =
    getKeyValue('common.price.displayInstalmentsPrice') &&
    INSTALMENT_LOCALES.includes(appContext.locale);
  const hidePriceInfo = displayInstalment && isOnLastPage;

  if (!isAppMounted) {
    return null;
  }

  return (
    <ActivitiesContext.Provider // TODO: cleanup !!
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        deleteService: (services, attendeeIds) => {
          track({
            labelGA: getActivityTypeName(services[0].pricingInfo.type),
            actionGA: 'removeFromCart',
          });
          // find the serviceIds to be deleted
          const serviceIds = services.reduce((acc, service) => {
            const [attendee] = service.pricingInfo.attendees.filter((a) => a.id === attendeeIds);
            return [
              ...acc,
              ...attendee.schedules.reduce(
                (scheduleAccumulator, schedule) =>
                  schedule.timeSlots.reduce(
                    (timeSlotAccumulator, timeSlot) =>
                      timeSlotAccumulator.includes(timeSlot.id)
                        ? timeSlotAccumulator
                        : [...timeSlotAccumulator, timeSlot.id],
                    scheduleAccumulator,
                  ),
                acc,
              ),
            ];
          }, []);
          return dispatch(
            deleteAdditionalServices({
              showNotification: true,
              serviceIds,
              attendeeIds,
              appContext,
            }),
          );
        },
        currency: proposal.price.currency,
        trackingData: TRACK_BE_EVENT_GA_ACTIVITIES,
      }}
    >
      <BottomNavbarLayer
        addedServicesNumber={addedServicesNumber}
        continueBtn={continueBtn}
        backLabel={!appContext.isCmta ? t('common.backNavigation') : ''}
        buyNowPayLaterPricing={
          enableBuyNowPayLaterProvider && (
            <BuyNowPayLaterPricing
              isPaymentPage={isOnLastPage}
              proposal={proposal}
              price={price}
              iconClassname={classnames('-bottom-[0.375rem] ml-5', {
                'bottom-[1.225rem]': isMobile && hasSelectedRate,
                '-bottom-[0.4rem]': isDesktop,
              })}
            />
          )
        }
        closeLabel={t('booking.priceDetails.packagePrice.closeReceipt')}
        continueAction={continueAction}
        continueLabel={isOnLastPage ? paymentLabel : t('common.continue')}
        forwardRef={layerRef}
        hasSelectedRate={hasSelectedRate}
        hideContinueButton={hideContinueButton}
        isDisabled={isDisabled}
        isFavorite={isFavorite}
        isNextStep={isNextStep}
        isPriceLoading={isPriceLoading}
        navbarRef={navbarRef}
        nextPage={nextPage}
        onClickFavorite={onClickFavorite}
        onCloseAllLayers={() => setShouldCloseAllLayers(false)}
        openLabel={t('booking.priceDetails.packagePrice.openReceipt')}
        overwriteContinueButton={overwriteContinueButton}
        previousPage={previousPage}
        price={price}
        isOnLastPage={isOnLastPage}
        portalPosition={portalPosition}
        previousAction={previousAction}
        shouldCloseAllLayers={shouldCloseAllLayers}
        showPanel={shouldOpenPanel}
        toggleReceiptPanel={closePanel}
        trackClickOpenReceipt={() => {
          trackClickEvent(TRACK_EVENT_NAMES.CLICK_BOTTOM_BAR, {
            detail_click: 'open_receipt',
          });
        }}
      >
        {onClickFavorite && (
          <div className="fixed inset-x-0 top-[10vh] isolate w-full bg-white">
            <PriceTicketFavorite
              isFilled={isFavorite}
              isNextStep={isNextStep}
              isSticky
              onClick={onClickFavorite}
            />
          </div>
        )}
        {!hidePriceInfo && (
          <div className="flex flex-wrap items-end justify-between gap-10 p-20 py-10 lg:flex-nowrap lg:py-20">
            <div className="flex-1"></div>
            <div className="relative flex items-end gap-10">
              <BottomNavbarTotalPrice
                isLoading={isPriceLoading}
                price={price}
                showPrice={!!t('booking.priceDetails.packagePrice.closeReceipt')}
                className="items-center text-center"
              />
            </div>
            <div className="flex flex-1">
              {price && price.hasFlexRates && price.selectedRate ? (
                <div
                  className={classnames('rounded-pill px-10 py-5', {
                    'bg-green': [FLEX_RATES.STANDARD, FLEX_RATES.EBB].includes(price.selectedRate),
                    'bg-sienna': [FLEX_RATES.NRR, FLEX_RATES.FLR].includes(price.selectedRate),
                  })}
                >
                  {/* @ts-expect-error TODO(flow-migration) flow can't understand that at this point its a ProposalPrice type */}
                  <SelectedRate price={price} />
                </div>
              ) : null}
            </div>
          </div>
        )}
        <SiteContainer>
          <p className="mb-10 mt-30 font-brand text-24 text-black">
            {t('booking.summary.pageTitle')}
          </p>
          <div className="flex gap-20">
            <Icon className="text-green" name={ICONS.CHECK_FILLED} />
            <p className="mb-10 font-brand text-green">
              {t('booking.summary.discount.bestRateGuarantee')}
            </p>
          </div>
          {displayOptionAvailable && <OptionBlock />}
          <div
            className="mb-100 rounded-12 bg-white p-20 shadow-sm"
            data-testid="stay-summary-ticket"
          >
            <AccordionBlock
              trackingEventName={TRACK_EVENT_NAMES.CLICK_RECEIPT_TICKET}
              lines={getSummaryViewLinesByProposal({
                accommodationSummary: (
                  <AccommodationSummary
                    accommodations={proposal.accommodations}
                    resort={resort}
                    roomQuantity={roomQuantity}
                  />
                ),
                i18n,
                locale: appContext.locale,
                selectedProposal: proposal,
                isJoyViewOrUrbanOasisAllInclusive:
                  isJoyViewAllInclusive || isUrbanOasisAllInclusive,
                isUrbanOasisFoodAndBeverage,
              })}
            >
              <div className="mt-40">
                {shouldShowPriceDetails && (
                  <p className="mb-20 font-bold">
                    {t('booking.priceDetails.packagePrice.priceDetails')}
                  </p>
                )}
                <PriceDetailLines
                  trackingEventName={TRACK_EVENT_NAMES.CLICK_RECEIPT_TICKET}
                  priceDetail={getPriceDetail({
                    selectedProposal: proposal,
                    priceWithDonation,
                    showPriceDetailLines: shouldShowPriceDetails,
                    promoCode: displayPromoCode ? (
                      <div className="mb-20">
                        <DiscountsLine discounts={offers} />
                        {isPromoCodeEnabled && !!proposal && (
                          <PromoCode
                            proposalId={proposal.id}
                            customerId={parseInt(proposal.attendees.list[0].customerId, 10)}
                            discounts={discounts}
                          />
                        )}
                      </div>
                    ) : (
                      <div className="mb-20">
                        <DiscountsLine discounts={proposal.price.discounts} />
                      </div>
                    ),
                  })}
                />
              </div>
              {!!proposal?.greatMembersLoyaltyPoints && (
                <GreatMembersLoyaltyPoints
                  loyaltyPoints={proposal?.greatMembersLoyaltyPoints}
                  dataTestId="greatMembersLoyaltyPoints"
                />
              )}
            </AccordionBlock>
          </div>
        </SiteContainer>
        {reservedServicesByAttendee.length > 0 && (
          <SiteContainer size="small">
            <Title className="mb-10" theme="title-brand-n2">
              {t('booking.activities.myPackage.title')}
            </Title>
            <ReservedServicesList
              currency={proposal.price.currency}
              noEditMode={noEdit}
              reservedServicesByAttendee={reservedServicesByAttendee}
              scrollBackwardLabel={t('common.scrollBackward')}
              scrollForwardLabel={t('common.scrollForward')}
            />
          </SiteContainer>
        )}
      </BottomNavbarLayer>
    </ActivitiesContext.Provider>
  );
};
